import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { CalendarEvent, ServiceModule, TextServiceModule, UrlServiceModule, LeaderboardServiceModule, LeaderboardTable, LeaderboardEntry, FileServiceModule, TimeTrackingServiceModule, AbsenceServiceModule } from "../../types/serviceModuleTypes";
import {
  deleteServiceModuleThunk,
  updateServiceModuleOrderThunk,
  updateServiceModuleThunk,
  updateCalendarModuleThunk,
  fetchServiceModulesThunk,
  fetchCalendarEventsThunk,
  createCalendarEventThunk,
  updateCalendarEventThunk,
  deleteCalendarEventThunk,
  fetchFolderModuleThunk,
  fetchFileModuleThunk,
  fetchTextModuleThunk,
  fetchUrlModuleThunk,
  fetchLeaderboardModuleThunk,
  fetchLeaderboardActionModulesThunk,
  updateLeaderboardModuleThunk,
  fetchLeaderboardTableThunk,
  fetchLeaderboardDashboardThunk,
  fetchTimeTrackingModuleThunk,
  updateTimeTrackingModuleThunk,
  updateAbsenceModuleThunk,
} from "../thunk/serviceModulesThunk";

export interface ServiceModulesState {
  serviceModules: ServiceModule[];
  newestServiceModule?: ServiceModule;
  calendarEvents: CalendarEvent[];
  onSave: boolean;
  folderModules: Record<number, ServiceModule>;
  fileModules: Record<number, FileServiceModule>;
  textModules: Record<number, TextServiceModule>;
  urlModules: Record<number, UrlServiceModule>;
  leaderboardModules: Record<number, LeaderboardServiceModule>;
  leaderboardActionModules: any,
  leaderboardData: LeaderboardTable;
  leaderboardDashboardData: LeaderboardEntry[];
  timeTrackingModules: Record<number, TimeTrackingServiceModule>;
  absenceModules: Record<number, AbsenceServiceModule>;
}

const initialState: ServiceModulesState = {
  serviceModules: [],
  newestServiceModule: undefined,
  calendarEvents: [],
  onSave: true,
  folderModules: {},
  fileModules: {},
  textModules: {},
  urlModules: {},
  leaderboardModules: {},
  leaderboardActionModules: undefined,
  leaderboardData: {
    totalCount: 0,
    items: [],
  },
  leaderboardDashboardData: [],
  timeTrackingModules: {},
  absenceModules: [],
}

export const serviceModulesSlice = createSlice({
  name: "ServiceModuleStoreEntries",
  initialState,
  reducers: {
    setServiceModules: (state, action: PayloadAction<ServiceModule[]>) => {
      state.serviceModules = action.payload;
    },
    setNewestServiceModule: (state, action: PayloadAction<ServiceModule>) => {
      state.newestServiceModule = action.payload;
    },
    resetNewestServiceModule: (state) => {
      state.newestServiceModule = undefined;
    },
    createServiceModule: (state, action: PayloadAction<ServiceModule>) => {
      state.serviceModules = [action.payload, ...state.serviceModules];
    },
    deleteServiceModule: (state, action: PayloadAction<number>) => {
      state.serviceModules = state.serviceModules.filter(
        (instance) => instance.id !== action.payload
      );
    },
    updateServiceModule: (state, action: PayloadAction<ServiceModule>) => {
      state.serviceModules = state.serviceModules.map((instance) =>
        instance.id === action.payload.id ? action.payload : instance
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceModulesThunk.fulfilled, (state, action) => {
        state.serviceModules = action.payload as ServiceModule[];
      })
      .addCase(deleteServiceModuleThunk.fulfilled, (state, action) => {
        state.serviceModules = state.serviceModules.filter(
          (instance: ServiceModule) => instance.id !== action.payload
        );
      })
      .addCase(updateServiceModuleOrderThunk.fulfilled, (state, action) => {
        const orderedIds = action.payload;
        const orderedInstances = orderedIds.map((id) =>
          state.serviceModules.find((instance) => instance.id === id)
        );
        state.serviceModules = orderedInstances.filter(Boolean) as ServiceModule[];
      })
      .addCase(updateServiceModuleThunk.fulfilled, (state, action) => {
        const updated = action.payload as ServiceModule;
        state.serviceModules = state.serviceModules.map((instance) =>
          instance.id === updated.id ? updated : instance
        );
      })
      .addCase(updateCalendarModuleThunk.fulfilled, (state, action) => {
        state.serviceModules = state.serviceModules.map((instance) =>
          instance.id === action.payload.id ? action.payload as ServiceModule : instance
        );
      })
      .addCase(fetchCalendarEventsThunk.fulfilled, (state, action) => {
        state.calendarEvents = action.payload as CalendarEvent[];
      })
      .addCase(createCalendarEventThunk.fulfilled, (state, action) => {
        state.calendarEvents.push(action.payload);
      })
      .addCase(updateCalendarEventThunk.fulfilled, (state, action) => {
        const updatedEvent = action.payload as CalendarEvent;
        state.calendarEvents = state.calendarEvents.map((event) =>
          event.id === updatedEvent.id ? updatedEvent : event
        );
      })
      .addCase(deleteCalendarEventThunk.fulfilled, (state, action) => {
        state.calendarEvents = state.calendarEvents.filter(
          (event) => event.id !== action.payload
        );
      })
      .addCase(fetchFolderModuleThunk.fulfilled, (state, action) => {
        action.payload.forEach(folder => {
          state.folderModules[folder.id] = folder;
        });
      })
      .addCase(fetchFileModuleThunk.fulfilled, (state, action) => {
        const file = action.payload
        state.fileModules[file.id] = file;
      })
      .addCase(fetchTextModuleThunk.fulfilled, (state, action) => {
        const text = action.payload as TextServiceModule;
        state.textModules[text.id] = text;
      })
      .addCase(fetchUrlModuleThunk.fulfilled, (state, action) => {
        const url = action.payload as UrlServiceModule;
        state.urlModules[url.id] = url;
      })
      .addCase(fetchLeaderboardModuleThunk.fulfilled, (state, action) => {
        const leaderboard = action.payload as LeaderboardServiceModule;
        state.leaderboardModules[leaderboard.id] = leaderboard;
      })
      .addCase(updateLeaderboardModuleThunk.fulfilled, (state, action) => {
        const leaderboard = action.payload as LeaderboardServiceModule;
        state.leaderboardModules[leaderboard.id] = leaderboard;
      })
      .addCase(fetchLeaderboardActionModulesThunk.fulfilled, (state, action) => {
        state.leaderboardActionModules = action.payload;
      })
      .addCase(fetchLeaderboardTableThunk.fulfilled, (state, action) => {
        state.leaderboardData = {
          totalCount: action.payload.totalCount,
          items: action.payload.items,
        };
      })
      .addCase(fetchLeaderboardDashboardThunk.fulfilled, (state, action) => {
        state.leaderboardDashboardData = action.payload;
      })
      .addCase(fetchTimeTrackingModuleThunk.fulfilled, (state, action) => {
        if (action.payload) {
          const module = action.payload;
          state.timeTrackingModules[module.id] = module;
        }
      })
      .addCase(updateTimeTrackingModuleThunk.fulfilled, (state, action) => {
        const updatedModule = action.payload;
        state.timeTrackingModules[updatedModule.id] = updatedModule;
      })
      .addCase(updateAbsenceModuleThunk.fulfilled, (state, action) => {
        const updatedModule = action.payload;
        state.absenceModules[updatedModule.id] = updatedModule;
      });
  },
});

export const {
  setServiceModules,
  createServiceModule,
  deleteServiceModule,
  updateServiceModule,
  setNewestServiceModule,
  resetNewestServiceModule
} = serviceModulesSlice.actions;

export default serviceModulesSlice.reducer;
